import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInstagram } from 'react-icons/fa';
import type { NavigationProps, MenuItem } from './types';

const NavigationDesktop: React.FC<NavigationProps> = ({
  data,
  onLogoClick,
  onMenuItemClick,
  className = '',
}) => {
  if (!data) throw new Error('NavigationDesktop component requires data prop');

  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);

  /* -------------------- Scroll Detection (Throttled) -------------------- */
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* -------------------- Scroll To Section -------------------- */
  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const yOffset = -96;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  /* -------------------- Menu Click -------------------- */
  const handleMenuClick = (item: MenuItem) => {
    // Check if onMenuItemClick handled it (returns true)
    if (onMenuItemClick) {
      const handled = onMenuItemClick(item);
      if (handled) return; // Stop if handler returned true
    }
    if (item.onClick) return item.onClick();
    if (!item.href) return;

    if (item.href.startsWith('/#')) {
      const id = item.href.replace('/#', '');

      if (location.pathname === '/') {
        scrollToSection(id);
      } else {
        navigate('/');
        setTimeout(() => scrollToSection(id), 300);
      }
    } else {
      navigate(item.href);
    }
  };

  /* -------------------- Logo Click -------------------- */
  const handleLogoClickDefault = () => {
    if (onLogoClick) return onLogoClick();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        isScrolled
          ? 'bg-black/90 backdrop-blur-md shadow-lg border-b border-white/10'
          : 'bg-transparent'
      } ${className}`}
    >
      {/* Top Bar */}
      <div
        className={`flex items-center justify-between px-4 sm:px-6 md:px-10 xl:px-16 transition-all duration-200 ${
          isScrolled ? 'py-4' : 'py-6'
        }`}
      >
        {/* Left Side: Logo + Phone Number */}
        <div className="flex items-center gap-6">
          {/* Logo */}
          <button
            onClick={handleLogoClickDefault}
            className="flex items-center gap-3 cursor-pointer"
            aria-label="Go to homepage"
          >
            <img 
              src="/logo.jpg" 
              alt="Barber Innovations Logo" 
              className="h-[clamp(2.5rem,6vw,3.5rem)] w-[clamp(2.5rem,6vw,3.5rem)] rounded-full object-cover shadow-md"
            />

            <div className="text-white">
              <h1 className="font-bold tracking-widest text-[clamp(0.95rem,2.5vw,1.25rem)]">
                {data.logo.text}
              </h1>
              <p className="uppercase text-white/80 tracking-[0.25em] text-[clamp(0.6rem,1.8vw,0.75rem)]">
                {data.logo.subtext}
              </p>
            </div>
          </button>

          {/* Phone Number - Desktop */}
          <a
            href="tel:+17325816834"
            className="flex items-center gap-2 text-white/90 hover:text-primary transition-colors"
            aria-label="Call us"
          >
            <span className="material-symbols-outlined text-xl">phone</span>
            <span className="font-medium">(732) 581-6834</span>
          </a>
        </div>

        {/* Desktop Menu */}
        <ul className="flex items-center gap-6 text-white/90 font-medium">
          {data.menuItems.map((item, index) => (
            <li key={index}>
              <button
                onClick={() => handleMenuClick(item)}
                className={`px-4 py-2 transition-colors ${
                  item.highlight
                    ? 'bg-primary text-white rounded-full px-5 font-semibold'
                    : 'hover:text-primary'
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
          {/* Instagram Icon */}
          <li>
            <a
              href="https://www.instagram.com/Greg_thebarber7"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Visit our Instagram"
            >
              <FaInstagram className="text-2xl" />
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default NavigationDesktop;
